const isOfferExpired = (offer, currentTime = Date.now()) => {
  if (offer?.status === 'expired') {
    return true;
  }

  const validUntil = new Date(offer?.validUntil).getTime();
  return Number.isFinite(validUntil) && validUntil <= currentTime;
};

export const isProductUnavailableForOffer = product => Boolean(
  product?.hasPriceRequest
  || product?.warnings?.blocked_prices
  || product?.warnings?.blocked_stocks
  || product?.warnings?.blocked_orders
);

export const OFFER_PRODUCT_STATUS_TRANSLATIONS = {
  PRODUCT_NOT_FOUND: 'offers.cartStatusProductNotFound',
  PRODUCT_DELETED: 'offers.cartStatusProductDeleted',
  PRODUCT_BLOCKED: 'offers.cartStatusProductBlocked',
  PRODUCT_MISSING_PRICE: 'offers.cartStatusProductMissingPrice',
  PRODUCT_NOT_SOLD_BY_SUPPLIER: 'offers.cartStatusProductNotSoldBySupplier',
  SUPPLIER_PRODUCTS_NOT_ALLOWED: 'offers.cartStatusSupplierProductsNotAllowed',
  PRODUCT_OUT_OF_STOCK: 'offers.cartStatusProductOutOfStock',
  PRODUCT_INSUFFICIENT_STOCK: 'offers.cartStatusProductInsufficientStock',
  PRODUCT_QUANTITY_NOT_A_MULTIPLE: 'offers.cartStatusProductQuantityNotMultiple'
};

export const getOfferProductErrors = error => {
  const responseStatus = Number(error?.status);
  const isCartProductsNotAddable = responseStatus === 400
    && error?.info?.code === 'SHOPPING_CART_PRODUCTS_NOT_ADDABLE';
  if (responseStatus !== 422 && !isCartProductsNotAddable) return [];

  if (Array.isArray(error?.info?.errors)) {
    return error.info.errors
      .map(item => ({
        ...item,
        productId: item?.productId || item?.product_id,
        supplierId: item?.supplierId ?? item?.supplier_id ?? null
      }))
      .filter(item => item.productId && item.status);
  }

  const productErrors = error?.info?.products || {};
  return Object.entries(productErrors)
    .filter(([, item]) => item && typeof item === 'object' && item.status)
    .map(([productId, item]) => ({
      productId: item.productId || productId,
      supplierId: item.supplierId ?? null,
      status: item.status
    }));
};

export const isValidClientEmail = email => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email || '');

export const MAX_MARKUP_PERCENT = 10000;

export const clampMarkupPercent = value => Math.min(
  MAX_MARKUP_PERCENT,
  Math.max(0, Number(value) || 0)
);

export const generateOfferId = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${ Date.now().toString(36) }-${ Math.random().toString(36).slice(2, 10) }`;
};

export const generateOfferNumber = () => {
  const now = new Date();
  const dateTime = [
    now.getFullYear(),
    now.getMonth() + 1,
    now.getDate(),
    now.getHours(),
    now.getMinutes(),
    now.getSeconds()
  ].map(value => String(value).padStart(2, '0')).join('');
  const number = Math.floor(Math.random() * 90000) + 10000;

  return `OF-${ dateTime }-${ number }`;
};

export const getOfferValidUntil = (date = new Date()) => {
  const validUntil = new Date(date);
  validUntil.setDate(validUntil.getDate() + (validUntil.getDay() === 5 ? 3 : 1));
  return validUntil.toISOString();
};

export const VAT_RATE = 0.21;

export const roundMoney = value => Math.round((value + Number.EPSILON) * 100) / 100;

export const getProductPrice = product => {
  const candidates = [
    product.unitResellerPrice,
    product.ronPromoPrice,
    product.ronPrice,
    product.promoPrice,
    product.price?.price,
    product.price,
    product.unitPrice,
    product.catalogPrice
  ];
  const price = candidates.find(value => Number.isFinite(Number(value)));
  return Number(price || 0);
};

export const normalizeWishlistProductPrice = product => {
  const { supplierId, ...productData } = product;
  const normalizedProduct = product.hasPrice === false
    && supplierId !== null
    && supplierId !== undefined
    ? { ...productData, supplierId }
    : productData;

  if (
    product.unitResellerPrice !== null
    && product.unitResellerPrice !== undefined
    && Number.isFinite(Number(product.unitResellerPrice))
  ) return normalizedProduct;

  const quantity = Math.max(1, Number(product.quantity || product.minQuantity || 1));
  return {
    ...normalizedProduct,
    unitResellerPrice: getProductPrice(product) / quantity
  };
};

// Unit selling price in RON: an explicit custom price wins, otherwise NOD price + markup.
export const getUnitOfferPriceInRon = (item, defaultMarkupPercent = 0) => {
  if (item.unitCustomPrice !== null && item.unitCustomPrice !== undefined) {
    return Number(item.unitCustomPrice);
  }
  const markupPercent = item.markupPercent ?? defaultMarkupPercent ?? 0;
  return getProductPrice(item) * (1 + (Number(markupPercent) / 100));
};

export default isOfferExpired;
